import React from "react";
import { WidgetLayout } from "@/pages/Buy/WidgetLayout";

interface OrderSummaryProps {
  setShowOrderSummary: (show: boolean) => void;
  formData?: any;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ formData }) => {
  const handleWireInstructions = () => {
    const url =
      formData.currency === "USD"
        ? "https://storage.paytrie.com/Paytrie_USD_Wire_Instructions_(Canadian_Bank).pdf"
        : "https://storage.paytrie.com/Paytrie_CAD_Wire_Instructions.pdf";

    window.open(url, "_blank");
  };

  const getConfirmationUrl = () => {
    const network = formData?.network || "";
    switch (network.toUpperCase()) {
      case "SOL":
        return "https://solscan.io/address/";
      case "ARB":
        return "https://arbiscan.io/address/";
      case "POLY":
        return "https://polygonscan.com/address/";
      case "OPT":
        return "https://optimistic.etherscan.io/address/";
      case "AVAX":
        return "https://snowtrace.io/address/";
      case "CEL":
        return "https://celoscan.io/address/";
      default:
        return "https://etherscan.io/address/";
    }
  };

  return (
    <div className="md:max-w-[730px] max-w-[330px] mx-auto" data-testid="container-summary-container">
      <WidgetLayout showInteracLabel={false}>
        <div className="flex justify-center items-start min-h-screen max-w-[750px]" data-testid="container-summary-content">
          <div className="dark:text-white sm:p-8 w-full max-w-full sm:max-w-[700px] text-black">
            <h3 className="text-xl font-normal mb-2 text-center mt-20 md:mt-10" data-testid="container-summary-title">
              ORDER SUMMARY
            </h3>

            <div className="space-y-4 md:text-lg sm:text-sm mt-8" data-testid="container-summary-details">
              <p className="flex justify-between font-bold" data-testid="container-summary-date">
                <span>Date:</span>
                <span className="font-normal" data-testid="container-summary-date-value">
                  {formData?.currentTime || "N/A"}
                </span>
              </p>
              <p className="flex justify-between font-bold" data-testid="container-summary-amount">
                <span>Wire Amount:</span>
                <span className="font-normal" data-testid="container-summary-amount-value">
                  {formData?.amount || "N/A"}
                </span>
              </p>
              <p className="flex justify-between font-bold" data-testid="container-summary-currency">
                <span>Currency:</span>
                <span className="font-normal" data-testid="container-summary-currency-value">
                  {formData?.currency || "N/A"}
                </span>
              </p>
              <p className="flex justify-between font-bold" data-testid="container-summary-stablecoin">
                <span>Stablecoin:</span>
                <span className="font-normal" data-testid="container-summary-stablecoin-value">
                  {formData?.stablecoin || "N/A"}
                </span>
              </p>
              <p className="flex justify-between font-bold" data-testid="container-summary-network">
                <span>Network:</span>
                <span className="font-normal" data-testid="container-summary-network-value">
                  {formData?.network || "N/A"}
                </span>
              </p>
              <p className="flex justify-between font-bold" data-testid="container-summary-wallet">
                <span>Wallet:</span>
                <span className="min-w-0 break-words text-right font-normal" data-testid="container-summary-wallet-value">
                  {formData?.wallet ? (
                    <a
                      href={`${getConfirmationUrl()}${formData.wallet}#tokentxns`}
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      className="text-blue-500 break-all font-normal"
                      data-testid="container-summary-wallet-link"
                    >
                      {formData.wallet}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </span>
              </p>
            </div>

            <div className="mt-20 space-y-6" data-testid="container-summary-actions">
              <button
                className="w-full py-4 sm:py-6 bg-black text-white dark:bg-white dark:text-black border border-black text-center block transition-colors text-sm sm:text-base"
                onClick={handleWireInstructions}
                data-testid="container-summary-wire-instructions-button"
              >
                Wire Instructions
              </button>
            </div>
          </div>
        </div>
      </WidgetLayout>
    </div>
  );
};

export default OrderSummary;

/*
POTENTIAL IMPROVEMENTS:
1. Add better validation for form data
2. Implement a loading state while fetching data
3. Add error handling for missing or invalid form data
4. Implement proper confirmation before proceeding with the wire transfer
5. Add more detailed information about the transaction process
6. Improve accessibility with proper ARIA attributes
7. Consider adding a printer-friendly version of the order summary
8. Add the ability to email the wire instructions
9. Implement better mobile responsiveness for the summary layout
10. Add transaction tracking functionality
11. Consider adding a save/download PDF option
12. Implement proper state management for complex form data
13. Add proper types for form data instead of using 'any'
14. Consider adding an estimated completion time for the transaction
15. Implement proper error boundaries for component failures
*/
